import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { makeRequest } from 'services/request.service';
import { showAlert } from 'services/alerts.service';
import { useAuth } from 'auth/AuthContext';
import ModalComponent from 'components/common/ModalComponent';
import ButtonComponent from 'components/common/ButtonComponent';
import LabeledInputComponent from 'components/common/LabeledInputComponent';
import PropTypes from 'prop-types';

const GenerateReportModal = ({ isOpen, onClose, onGenerate }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [state, setState] = useState({
    reportType: 'crops-analytics',
    startDateTime: '',
    endDateTime: '',
    generating: false
  });

  const handleGenerate = async () => {
    // Validate dates
    if (!state.startDateTime || !state.endDateTime) {
      showAlert({ message: t('validation.selectDates'), icon: 'error' });
      return;
    }

    if (new Date(state.startDateTime) > new Date(state.endDateTime)) {
      showAlert({ message: t('validation.dateRange'), icon: 'error' });
      return;
    }

    setState(s => ({ ...s, generating: true }));

    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/reports/${user.farmId}/crops-analytics?startDateTime=${state.startDateTime}&endDateTime=${state.endDateTime}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        },
        responseType: 'blob'
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `crops_analytics_${state.startDateTime}_${state.endDateTime}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showAlert({ message: t('reports.generateSuccess'), icon: 'success' });

      // Reset form and close modal
      setState({
        reportType: 'crops-analytics',
        startDateTime: '',
        endDateTime: '',
        generating: false
      });
      onClose();

      // Refresh the reports list
      if (onGenerate) {
        onGenerate();
      }
    } catch (err) {
      showAlert({
        message: err.response?.data?.message || t('reports.generateFailed'),
        icon: 'error'
      });
      setState(s => ({ ...s, generating: false }));
    }
  };

  const handleClose = () => {
    if (!state.generating) {
      setState({
        reportType: 'crops-analytics',
        startDateTime: '',
        endDateTime: '',
        generating: false
      });
      onClose();
    }
  };

  return (
    <ModalComponent isOpen={isOpen} onClose={handleClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{t('reports.generateReport')}</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={state.generating}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Report Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('reports.reportType')}
            </label>
            <select
              value={state.reportType}
              onChange={e => setState(s => ({ ...s, reportType: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={state.generating}
            >
              <option value="crops-analytics">{t('reports.cropsAnalyticsReport')}</option>
            </select>
          </div>

          {/* Start Date */}
          <LabeledInputComponent
            label={t('reports.startDateTime')}
            type="datetime-local"
            value={state.startDateTime}
            onChange={e => setState(s => ({ ...s, startDateTime: e.target.value }))}
            disabled={state.generating}
            required
          />

          {/* End Date */}
          <LabeledInputComponent
            label={t('reports.endDateTime')}
            type="datetime-local"
            value={state.endDateTime}
            onChange={e => setState(s => ({ ...s, endDateTime: e.target.value }))}
            disabled={state.generating}
            required
          />

          {/* Info Message */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">{t('reports.reportInfo')}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={state.generating}
          >
            {t('common.cancel')}
          </button>
          <ButtonComponent onClick={handleGenerate} disabled={state.generating}>
            {state.generating ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {t('reports.generating')}
              </>
            ) : (
              t('reports.generateReport')
            )}
          </ButtonComponent>
        </div>
      </div>
    </ModalComponent>
  );
};

GenerateReportModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onGenerate: PropTypes.func
};

export default GenerateReportModal;
